import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessagesSquare, Send } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { api, fmtTs } from "../../lib/api";
import { Avatar } from "./bits";
import { Input } from "../ui/input";

export default function Chat({ scope, refId, title = "Discussion", height = "h-56" }) {
  const { user, loadNotifications } = useApp();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get("/chat", { params: { scope, refId } });
    setMsgs(r.data);
  }, [scope, refId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [msgs.length]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.post("/chat", { scope, refId, userId: user.id, text });
      setMsgs((m) => [...m, r.data]);
      setText("");
      loadNotifications();
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid={`chat-${scope}`}>
      <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900">
        <MessagesSquare className="h-[18px] w-[18px] text-[#00573f]" /> {title}
      </h3>
      <div className={`${height} space-y-4 overflow-y-auto pr-1`}>
        {msgs.length === 0 && <p className="pt-6 text-center text-sm text-slate-400">No messages yet — start the conversation.</p>}
        {msgs.map((m) => {
          const mine = m.userId === user?.id;
          return (
            <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
              <Avatar person={{ name: m.userName, initials: m.initials, color: m.color }} size={30} />
              <div className={`max-w-[80%] ${mine ? "text-right" : ""}`}>
                <p className="text-xs font-semibold text-slate-700">
                  {m.userName} <span className="ml-1 text-[10px] font-normal text-slate-400">{fmtTs(m.ts)}</span>
                </p>
                <p
                  className={`mt-1 inline-block whitespace-pre-wrap rounded-xl px-3 py-2 text-left text-sm ${
                    mine ? "bg-[#00573f] text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {m.text}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="mt-4 flex gap-2 border-t border-slate-100 pt-4">
        <Input
          data-testid={`chat-input-${scope}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder="Write a message... (Enter to send)"
          className="h-10"
        />
        <button
          data-testid={`chat-send-${scope}`}
          onClick={send}
          disabled={!text.trim() || sending}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#00573f] text-white transition-colors hover:bg-[#004330] disabled:opacity-40"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
